if (!customElements.get("product-card-slider")) {
  class ProductCardSlider extends HTMLElement {
    constructor() {
      super();
      this._swiperInstance = null;

      this._isNestedInSlider = false;
      this._isMobile = false;

      this._onResizeListener = null;
    }

    connectedCallback() {
      this._paginationElement = this.querySelector(this.getAttribute("pagination"))

      this._prevButtonElement = this.querySelector(this.getAttribute("prev-button"));
      this._nextButtonElement = this.querySelector(this.getAttribute("next-button"));

      this._isNestedInSlider = this.getAttribute("is-nested-in-slider") === "true";

      this._isMobile = window.innerWidth < ProductCardSlider._breakpoints.desktop;

      this._onResizeListener = this._onResize.bind(this);
      document.addEventListener("window:resize", this._onResizeListener);

      if (this._isNestedInSlider && this._isMobile) {
        return;
      }

      ProductCardSlider.observe(this);
    }

    disconnectedCallback() {
      this._destroy();

      if (this._onResizeListener) document.removeEventListener("window:resize", this._onResizeListener);
    }

    _init() {
      if (this._swiperInstance) return;
      if (this._isNestedInSlider && this._isMobile) return;

      this._swiperInstance = new window.Swiper(this, this._getOptions());
    }

    _destroy() {
      if (!this._swiperInstance) return;

      this._swiperInstance.destroy(true, false);
      this._swiperInstance = null;
    }

    _getOptions() {
      return {
        slidesPerView: "auto",
        spaceBetween: 0,
        pagination: false,
        navigation: false,
        loop: true,
        ...(this._paginationElement && {
          pagination: {
            el: this._paginationElement,
            clickable: false
          }
        }),
        ...(this._prevButtonElement && this._nextButtonElement && {
          navigation: {
            prevEl: this._prevButtonElement,
            nextEl: this._nextButtonElement
          }
        }),
        ...(this._isNestedInSlider && {
          allowTouchMove: false,
        })
      }
    }

    _onResize() {
      this._isMobile = window.innerWidth < ProductCardSlider._breakpoints.desktop;

      if (this._isNestedInSlider && this._isMobile) {
        this._destroy();
      } else {
        this._init();
      }
    }

    static get _breakpoints() {
      return {
        desktop: 992,
        mobile: 375
      }
    }

    static _observer = null;

    static observe(el) {
      if (!ProductCardSlider._observer) {
        ProductCardSlider._observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            const slider = entry.target;
            if (entry.isIntersecting && !slider._swiperInstance) {
              if (slider._isNestedInSlider && slider._isMobile) {
                return;
              }

              slider._init();
              ProductCardSlider._observer.unobserve(slider);
            }
          });
        }, { rootMargin: "200px 0px" });
      }

      ProductCardSlider._observer.observe(el);
    }
  }

  customElements.define("product-card-slider", ProductCardSlider);
}

if (!customElements.get("product-card-size-availability")) {
  class ProductCardSizeAvailability extends HTMLElement {
    static selectors = {
      panel: "[data-panel]",
      open: "[data-panel-open]",
      close: "[data-panel-close]",
    };

    static modifiers = {
      panelHidden: "product-card__size-availability-panel--hidden"
    };

    constructor() {
      super();

      this._onToggleListener = this._onToggle.bind(this);
    }

    connectedCallback() {
      this._panelElement = this.querySelector(ProductCardSizeAvailability.selectors.panel);
      this._panelOpenElement = this.querySelector(ProductCardSizeAvailability.selectors.open);
      this._panelCloseElement = this.querySelector(ProductCardSizeAvailability.selectors.close);

      if (!this._panelElement || !this._panelOpenElement || !this._panelCloseElement) {
        return;
      }

      this._panelOpenElement.addEventListener("click", this._onToggleListener);
      this._panelCloseElement.addEventListener("click", this._onToggleListener);
    }

    disconnectedCallback() {
      if (!this._panelOpenElement || !this._panelCloseElement) {
        return;
      }

      this._panelOpenElement.removeEventListener("click", this._onToggleListener);
      this._panelCloseElement.removeEventListener("click", this._onToggleListener);
    }

    _onToggle() {
      const isExpanded = this._panelElement.classList.contains(ProductCardSizeAvailability.modifiers.panelHidden);

      this._panelElement.classList.toggle(ProductCardSizeAvailability.modifiers.panelHidden, !isExpanded);
      this._panelElement.inert = !isExpanded;

      if (!isExpanded) {
        const focusableElements = this._panelElement.getFocusableElements();
        const firstFocusableElement =  focusableElements[0];

        setTimeout(() => {
          firstFocusableElement.focus();
        }, 400);

        this._panelElement.trapFocus();
      } else {
        this._panelElement.removeTrapFocus();
      }

      this._panelOpenElement.setAttribute("aria-expanded", (!isExpanded).toString());
    }
  }

  customElements.define("product-card-size-availability", ProductCardSizeAvailability);
}

export class SwatchesDrawer extends HTMLElement {
  constructor() {
    super();

    this._originalParentElement = null;
    this._nextSiblingElement = null;

    this._onControlClicked = this._onControlClicked.bind(this);
  }

  get isOpen() {
    return this.hasAttribute("open");
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get targetElement() {
    return document.body;
  }

  connectedCallback() {
    this.controls.forEach((control) =>
      control.addEventListener("click", this._onControlClicked)
    );
  }

  disconnectedCallback() {
    this.controls.forEach((control) =>
      control.removeEventListener("click", this._onControlClicked)
    );
  }

  async open() {
    this._beforeOpen();
    await this._moveToTargetElement();
    this._applyOpenState();
    this._afterOpen();
  }

  async close() {
    await this._removeOpenState();
    this._afterClose();
    this._restorePosition();
  }

  _onControlClicked() {
    this.isOpen ? this.close() : this.open();
  }

  _beforeOpen() {
    this._previouslyFocusedElement = document.activeElement;

    this._originalParentElement = this.parentNode;
    this._nextSiblingElement = this.nextSibling;

    this.removeAttribute("hidden");
    this.removeAttribute("inert");
  }

  _moveToTargetElement() {
    this.targetElement.appendChild(this);

    // Await transition end
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  }

  _applyOpenState() {
    this.setAttribute("open", "");
    this.controls.forEach(control =>
      control.setAttribute("aria-expanded", "true")
    );

    const scrollableContent = this.querySelector("[data-scrollable-content]");
    if (scrollableContent) {
      bodyScrollLock.disableBodyScroll(scrollableContent);
    }
  }

  _afterOpen() {
    const focusableElements = this.getFocusableElements();
    const firstFocusableElement = focusableElements[0];

    requestAnimationFrame(() => {
      firstFocusableElement.focus();
    });

    this.trapFocus();
  }

  _removeOpenState() {
    this.removeAttribute("open");
    this.controls.forEach(control =>
      control.setAttribute("aria-expanded", "false")
    );

    const scrollableContent = this.querySelector("[data-scrollable-content]");
    if (scrollableContent) {
      bodyScrollLock.enableBodyScroll(scrollableContent);
    }

    // Await transition end
    return new Promise((resolve) => setTimeout(resolve, 300));
  }

  _afterClose() {
    if (this._previouslyFocusedElement && document.contains(this._previouslyFocusedElement)) {
      this._previouslyFocusedElement.focus();
    }

    this._previouslyFocusedElement = null;

  }

  _restorePosition() {
    if (!this._originalParentElement) return;

    if (this._nextSiblingElement) {
      this._originalParentElement.insertBefore(this, this._nextSiblingElement);
    } else {
      this._originalParentElement.appendChild(this);
    }

    this.setAttribute("hidden", "");
    this.setAttribute("inert", "");

    this._originalParentElement = null;
    this._nextSiblingElement = null;
  }
}

if (!customElements.get("swatches-drawer")) {
  customElements.define("swatches-drawer", SwatchesDrawer);
}
